import type { Chord, NoteEvent } from '@tunasong/music-lib'
import { chordToEvents } from '../player/chord-to-events.js'
import { useStabScheduler } from '../player/stab-scheduler.js'

/**
 * Function Quiz v1 audition: two chord stabs (quiz-chord then tonic-chord)
 * separated by GAP_MS. Half-note each, ~2600 ms total at 100 BPM. One-shot.
 *
 * Spec: docs/superpowers/specs/2026-05-18-function-quiz-design.md §4.4
 */
const TEMPO_BPM = 100
const BEAT_MS = 60_000 / TEMPO_BPM
const HALF_NOTE_MS = BEAT_MS * 2 // 1200 ms — quiz chord and tonic each
const GAP_MS = 200 // brief release between chords (Bowie §8.2 verdict)

export interface QuizStab {
  events: NoteEvent[]
  delayMs: number
}

export const buildQuizAuditionStabs = (quizChord: Chord, tonicChord: Chord): QuizStab[] => [
  { events: chordToEvents(quizChord, '2n'), delayMs: 0 },
  { events: chordToEvents(tonicChord, '2n'), delayMs: HALF_NOTE_MS + GAP_MS },
]

export interface UseFunctionQuizPlaybackProps {
  quizChord: Chord | null
  tonicChord: Chord | null
}

/**
 * Hook returning `{ play, stop }`. Thin wrapper over `useStabScheduler`.
 * Null chords → empty stab list → no-op play.
 */
export const useFunctionQuizPlayback = (
  props: UseFunctionQuizPlaybackProps
): { play: () => void; stop: () => void } => {
  const { play, stop } = useStabScheduler({
    buildStabs: () =>
      props.quizChord && props.tonicChord
        ? buildQuizAuditionStabs(props.quizChord, props.tonicChord)
        : [],
  })
  return { play, stop }
}
